import Icon, { IconsVariant } from "@/Components/Icon/Icon";
import { cn } from "@/shared/utils/cn";
import { createContext, ReactNode, useContext } from "react";

export interface SettingsPanelMeta {
  title: string;
  description?: string;
  icon: IconsVariant;
}

export const SettingsPanelContext = createContext<SettingsPanelMeta | null>(
  null
);

interface PanelHeaderProps {
  actions?: ReactNode;
}

/** Title of the active settings page, with room for page-level actions. */
export const PanelHeader = ({ actions }: PanelHeaderProps) => {
  const meta = useContext(SettingsPanelContext);
  if (!meta) return null;

  return (
    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-4">
        <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-primary text-white">
          <Icon
            className="size-8 fill-[var(--button_text_color)]"
            variants={meta.icon}
          />
        </div>
        <div className="min-w-0">
          <h1 className="m-0 text-[2.4rem] leading-tight">{meta.title}</h1>
          {meta.description && (
            <p className="m-0 text-small text-text-tertiary">
              {meta.description}
            </p>
          )}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
    </div>
  );
};

interface SaveBarProps {
  children: ReactNode;
  hint?: string;
  className?: string;
}

/** Floating bar that keeps the save button in reach on long forms. */
export const SaveBar = ({ children, hint, className }: SaveBarProps) => (
  <div
    className={cn(
      "sticky bottom-6 z-10 mt-8 flex items-center justify-between gap-4 rounded-2xl border border-grey-blue-light bg-white px-6 py-4 shadow-[0_0.8rem_2.4rem_rgba(0,0,0,0.12)]",
      className
    )}
  >
    <div className="hidden text-small text-text-tertiary sm:block">{hint}</div>
    <div className="ml-auto flex flex-wrap gap-3">{children}</div>
  </div>
);

export const PanelState = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-[30rem] items-center justify-center rounded-2xl border border-grey-blue-light bg-white">
    {children}
  </div>
);
